import { StartDateSchema } from "@pax/world";
import { z } from "zod";
import {
  CatalogFileName,
  CatalogSlug,
  ImageSource,
  PaginationQuery,
  PresetId,
  PresetVersionId,
  ProfileId,
  Sha256,
  Timestamp,
  type VideoSet,
  videoFileFields,
} from "../../common";
import { CreatedFrom } from "../../common/created-from";
import { ItemModeration } from "../../moderation/v1alpha1/schemas";
import { PresetCategory } from "./categories";

/** How long a version is its authors' alone before anyone may copy it, unless they allow it sooner. */
export const COPY_PROTECTION_DAYS = 30;

/** The most characters a version number runs to. */
export const VERSION_NUMBER_LENGTH = 5;

/** The most tags a version carries, and the most characters one runs to. */
export const PRESET_TAG_MAX_COUNT = 8;
export const PRESET_TAG_MAX_CHARACTERS = 40;

export const PresetTag = z
  .string()
  .regex(new RegExp(`^[a-z0-9](?:[a-z0-9-]{0,${PRESET_TAG_MAX_CHARACTERS - 2}}[a-z0-9])?$`))
  .meta({
    id: "PresetTag",
    description: `A tag its authors wrote: lowercase letters, digits, and hyphens, 1 to ${PRESET_TAG_MAX_CHARACTERS} characters, starting and ending with a letter or digit. Spaces are written as hyphens.`,
    example: "cold-war",
  });
export type PresetTag = z.infer<typeof PresetTag>;

export const PresetTags = z
  .array(PresetTag)
  .max(PRESET_TAG_MAX_COUNT)
  .refine(tags => new Set(tags).size === tags.length, "Tags must not repeat.")
  .meta({
    id: "PresetTags",
    description: `Up to ${PRESET_TAG_MAX_COUNT} different tags, in the order its authors gave them.`,
    example: ["cold-war", "naval"],
  });

/** What a version's authors number it: digits and periods, such as 1.2, at most 5 characters. */
export const VersionNumber = z
  .string()
  .trim()
  .min(1, { error: "Enter a version number", abort: true })
  .regex(/^[\d.]*$/, { error: "Digits and periods only", abort: true })
  .max(VERSION_NUMBER_LENGTH, { error: `${VERSION_NUMBER_LENGTH} characters max`, abort: true })
  .refine(text => !text.startsWith(".") && !text.includes(".."), {
    error: "Put digits between periods, like 1.2",
    abort: true,
  })
  .regex(/^\d+(\.\d+)*$/, "Finish the number, like 1.2")
  .meta({
    id: "VersionNumber",
    description: "What its authors number it, such as 1.2.",
    example: "1.2",
  });
export type VersionNumber = z.infer<typeof VersionNumber>;

export const ArtworkTint = z
  .strictObject({
    hue: z.number().min(0).lt(360).meta({ description: "Degrees around the OKLCH wheel." }),
    chroma: z
      .number()
      .min(0)
      .max(1)
      .meta({ description: "How colourful the artwork is. A monochrome one is 0." }),
  })
  .meta({
    id: "ArtworkTint",
    description:
      "The colour artwork is mostly made of. How light the result should be is left to the reader's theme, so only the hue and its strength are carried.",
  });
export type ArtworkTint = z.infer<typeof ArtworkTint>;

export const TimedArtworkTint = ArtworkTint.extend({
  atMs: z.number().int().nonnegative(),
}).meta({
  id: "TimedArtworkTint",
  description: "One tint beginning at a presented-media timestamp.",
});
export type TimedArtworkTint = z.infer<typeof TimedArtworkTint>;

const TintTrack = z
  .array(TimedArtworkTint)
  .min(1)
  .refine(
    track =>
      track[0]?.atMs === 0 &&
      track.every((frame, index) => {
        const previous = track[index - 1];
        return index === 0 || (previous !== undefined && frame.atMs > previous.atMs);
      }),
    { message: "tint track must start at zero and increase strictly" },
  );

const LandingDurationMs = z.number().int().positive().meta({
  description: "How long one loop of the video lasts, in milliseconds.",
  example: 10_000,
});

export const LandingVideoSource = z
  .strictObject({
    url: z.url().meta({ description: "Where the MP4 is served." }),
    ...videoFileFields,
  })
  .meta({ id: "LandingVideoSource", description: "One size of the landing video." });
export type LandingVideoSource = z.infer<typeof LandingVideoSource>;

export const LandingVideo = z
  .strictObject({
    sources: z.array(LandingVideoSource).min(1).meta({
      description:
        "The video at half and at full size, smallest first. Play the smallest that covers its space.",
    }),
    durationMs: LandingDurationMs,
  })
  .meta({
    id: "LandingVideo",
    description:
      "The landing artwork in motion: a silent AV1 MP4 that loops. The web player uses the browser's decoder when available, and on the preset's own page a software decoder otherwise.",
  });
export type LandingVideo = z.infer<typeof LandingVideo>;

/** A landing video as the sources a player chooses from. */
export function landingVideoSet(video: LandingVideo): VideoSet {
  return {
    sources: video.sources.map(({ bytes: _, ...source }) => source),
    durationSeconds: video.durationMs / 1000,
    hasAudio: false,
  };
}

export const ArtworkSet = z
  .strictObject({
    sources: z.array(ImageSource).min(1).meta({
      description: "Every size of the artwork as AVIF, smallest first.",
    }),
    thumbhash: z.base64().meta({
      description: "The artwork a few pixels wide, to draw until a source arrives.",
    }),
  })
  .meta({ id: "ArtworkSet", description: "Preset artwork in several sizes." });
export type ArtworkSet = z.infer<typeof ArtworkSet>;

export const LandingMedia = z
  .strictObject({
    poster: ArtworkSet.meta({
      description: "The landing still, or the frame shown until its video can play.",
    }),
    video: LandingVideo.nullable(),
    tintTrack: TintTrack,
  })
  .meta({
    id: "LandingMedia",
    description:
      "Optional hero media and its publication-time colour track. A still has one tint at zero; a video carries timed tints.",
  });
export type LandingMedia = z.infer<typeof LandingMedia>;

export const PresetAuthor = z
  .object({
    profileId: ProfileId,
    username: z.string().nullable().meta({
      description: "The author's handle, or null while they have not claimed one.",
    }),
    displayName: z.string().min(1),
  })
  .meta({
    id: "PresetAuthor",
    description: "Who published the scenario, as their profile reads right now.",
  });
export type PresetAuthor = z.infer<typeof PresetAuthor>;

/**
 * What every view of a scenario shows. A listing and a detail are both this
 * plus the one thing they are for, so a page opened from a rail can be drawn
 * from what the rail already had.
 */
export const PresetStorefront = z.object({
  id: PresetId,
  slug: CatalogSlug,
  title: z.string().min(1),
  description: z.string(),
  author: PresetAuthor,
  cover: ArtworkSet.nullable(),
  landing: LandingMedia.nullable(),
  coverTint: ArtworkTint.nullable(),
  mapPreview: ArtworkSet.nullable().meta({
    description:
      "A still of the opening map, taken when the version was published. Shown in place of the live map, which is megabytes of geometry, until the reader asks for it.",
  }),
  regionCount: z.number().int().nonnegative(),
  entityCount: z.number().int().nonnegative(),
  startDate: StartDateSchema,
  category: PresetCategory,
  tags: PresetTags,
  publishedAt: Timestamp.meta({ description: "When the version shown was published." }),
  copyableFrom: Timestamp.meta({
    description: `When anyone may copy the version shown: as it was published, if its authors allow early copies, or ${COPY_PROTECTION_DAYS} days after.`,
  }),
  gamesStarted: z.number().int().nonnegative().meta({
    description: "Games started from any of its versions, not counting evaluations.",
  }),
  roundsPlayed: z.number().int().nonnegative().nullable().meta({
    description:
      "Rounds played in those games, as game events count them: close, not exact. Null when they couldn't be counted just now.",
  }),
});
export type PresetStorefront = z.infer<typeof PresetStorefront>;

export const PresetListing = PresetStorefront.extend({
  liveVersionId: PresetVersionId.meta({ description: "The live version the listing shows." }),
}).meta({ id: "PresetListing", description: "Storefront metadata for one published scenario." });
export type PresetListing = z.infer<typeof PresetListing>;

export const WorldDownload = z
  .object({
    url: z.string().startsWith("/"),
    digest: Sha256,
    bytes: z.number().int().positive(),
    throughRevision: z.number().int().nonnegative(),
  })
  .meta({
    id: "WorldDownload",
    description: "The immutable world artifact for this preset version.",
  });
export type WorldDownload = z.infer<typeof WorldDownload>;

export const PresetDetail = PresetStorefront.extend({
  presetVersionId: PresetVersionId,
  version: z.number().int().positive().meta({
    description: "Which version it is, counting each one published, from 1.",
  }),
  versionNumber: VersionNumber.meta({
    description: "What its authors number this version, such as 1.2.",
  }),
  versionName: z.string().meta({
    description: "What its authors called this version, or empty when they didn't name it.",
  }),
  changeLog: z.string().meta({ description: "What its authors said changed in this version." }),
  createdFrom: CreatedFrom,
  world: WorldDownload,
}).meta({
  id: "PresetDetail",
  description: "Storefront metadata plus one immutable world artifact.",
});
export type PresetDetail = z.infer<typeof PresetDetail>;

export const ListPresetTagsQuery = z
  .strictObject({
    prefix: z
      .string()
      .trim()
      .toLowerCase()
      .min(1)
      .max(PRESET_TAG_MAX_CHARACTERS)
      .optional()
      .meta({ description: "Only tags starting with this, for autocomplete.", example: "co" }),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .meta({ id: "ListPresetTagsQuery", description: "Which tags to count." });
export type ListPresetTagsQuery = z.infer<typeof ListPresetTagsQuery>;

export const PresetTagCount = z
  .strictObject({
    tag: PresetTag,
    presetCount: z.int().positive().meta({
      description: "Listed presets whose live version carries the tag.",
      example: 12,
    }),
  })
  .meta({ id: "PresetTagCount", description: "A tag and how many listed presets carry it." });
export type PresetTagCount = z.infer<typeof PresetTagCount>;

export const ListPresetTagsResponse = z
  .strictObject({
    tags: z.array(PresetTagCount).meta({ description: "The most used tags first." }),
  })
  .meta({ id: "ListPresetTagsResponse", description: "Tags across the listed presets." });
export type ListPresetTagsResponse = z.infer<typeof ListPresetTagsResponse>;

export const PresetSlugParams = z
  .strictObject({ slug: CatalogSlug })
  .meta({ id: "PresetSlugParams", description: "A route parameter naming one preset." });
export type PresetSlugParams = z.infer<typeof PresetSlugParams>;

export const ListPresetsQuery = PaginationQuery.extend({
  q: z.string().trim().toLowerCase().min(1).max(100).optional(),
  ownerId: ProfileId.optional().meta({
    description: "Only presets this profile owns.",
  }),
});
export type ListPresetsQuery = z.infer<typeof ListPresetsQuery>;

export const ListPresetsResponse = z
  .object({
    items: z.array(PresetListing),
    nextCursor: z.string().nullable(),
  })
  .meta({ id: "ListPresetsResponse", description: "One page of scenarios." });
export type ListPresetsResponse = z.infer<typeof ListPresetsResponse>;

export const PresetViewer = z
  .strictObject({
    canSeeEarnings: z.boolean().meta({
      description:
        "Whether the signed-in user may open this preset's earnings: `true` for its team and for staff who can view credits.",
      example: false,
    }),
  })
  .meta({
    id: "PresetViewer",
    description:
      "The signed-in user's relationship to this preset, for deciding which links to render. Absent without a session.",
  });
export type PresetViewer = z.infer<typeof PresetViewer>;

export const GetPresetResponse = z
  .object({
    preset: PresetDetail,
    viewer: PresetViewer.optional(),
    moderation: ItemModeration.optional().meta({
      description:
        "The preset's moderation state, such as a removal. Returned only to its owner, by Get a preset.",
    }),
  })
  .meta({ id: "GetPresetResponse", description: "One preset and its pinned world artifact." });
export type GetPresetResponse = z.infer<typeof GetPresetResponse>;

export const PresetVersionParams = z.strictObject({
  versionId: PresetVersionId,
});
export type PresetVersionParams = z.infer<typeof PresetVersionParams>;

export const PresetVersionFileParams = PresetVersionParams.extend({
  file: CatalogFileName,
});
export type PresetVersionFileParams = z.infer<typeof PresetVersionFileParams>;
